import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ReplayIcon from '@mui/icons-material/Replay';
import PopupTitle from '../common/popup_title';
import { nutritionOneLineSummary } from '../../services/nutrition';

const IngredientStructuredDataEditHomePage = ({
  fullText,
  linkedIngredientName,
  structuredIngredientName,
  unitOfMeasure,
  supportedMeasures,
  quantity,
  preparation,
  nutrition,
  errors,
  onDismiss,
  onClickUpdate,
  onClickRewriteIngredientLine,
  onClickSearchIngredient,
  onChangeStructuredIngredientName,
  onChangeUnitOfMeasure,
  onChangeQuantity,
  onChangePreparation,
}) => {
  const isValid = Object.values(errors).every((error) => !error);

  return (
    <Grid container>
      <Grid item xs={12}>
        <PopupTitle
          backButtonEnabled={!!onDismiss}
          closeButtonEnabled
          onClickBack={onDismiss}
          onClickClose={onDismiss}
          titleText="Ingredient Detail"
        />
      </Grid>
      <Grid item xs={12}>
        <DialogContent style={{ fontSize: '14px' }}>
          <Grid container>
            <Grid item xs={12}>
              <h4>Displayed ingredient line</h4>
            </Grid>
            <Grid item xs={12}>
              {fullText}
            </Grid>
            <Grid item xs={12}>
              <h4>Recognised food data</h4>
            </Grid>
            <Grid item xs={6} style={{ display: 'flex', padding: '5px 0' }}>
              <p style={{ margin: 'auto 0' }}>Ingredient:</p>
            </Grid>
            <Grid item xs={6} style={{ display: 'flex', padding: '5px 0' }}>
              <TextField
                style={{
                  margin: 'auto 0',
                  ...(errors.linkedIngredientName
                    ? { border: '1px solid red' }
                    : {}),
                }}
                variant="standard"
                fullWidth
                InputProps={{
                  readOnly: true,
                  style: {
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: 'var(--space-primary-color)',
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton tabIndex={-1} edge="end">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                value={linkedIngredientName}
                onClick={onClickSearchIngredient}
              />
            </Grid>
            <Grid item xs={6} style={{ display: 'flex', padding: '5px 0' }}>
              <p style={{ margin: 'auto 0' }}>Displayed ingredient:</p>
            </Grid>
            <Grid item xs={6} style={{ display: 'flex', padding: '5px 0' }}>
              <TextField
                style={{
                  margin: 'auto 0',
                  ...(errors.structuredIngredientName
                    ? { border: '1px solid red' }
                    : {}),
                }}
                InputProps={{
                  style: {
                    fontSize: '14px',
                    color: 'var(--space-primary-color)',
                  },
                }}
                variant="standard"
                fullWidth
                value={structuredIngredientName}
                onChange={(ev) =>
                  onChangeStructuredIngredientName(ev.target.value)
                }
              />
            </Grid>
            <Grid item xs={6} style={{ display: 'flex', padding: '5px 0' }}>
              <p style={{ margin: 'auto 0' }}>Available measures:</p>
            </Grid>
            <Grid
              item
              xs={6}
              style={{
                display: 'flex',
                padding: '5px 0',
              }}>
              <Select
                style={{
                  margin: 'auto 0',
                  color: 'var(--space-primary-color)',
                  ...(errors.unitOfMeasure ? { border: '1px solid red' } : {}),
                }}
                variant="standard"
                value={unitOfMeasure}
                label="Measure"
                onChange={(ev) => onChangeUnitOfMeasure(ev.target.value)}>
                {supportedMeasures.map((measure) => (
                  <MenuItem
                    key={measure.unitOfMeasure}
                    value={measure.unitOfMeasure}>
                    {measure.unitOfMeasure}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={6} style={{ display: 'flex', padding: '5px 0' }}>
              <p style={{ margin: 'auto 0' }}>Qty:</p>
            </Grid>
            <Grid item xs={6} style={{ display: 'flex', padding: '5px 0' }}>
              <TextField
                style={{
                  margin: 'auto 0',
                  ...(errors.quantity ? { border: '1px solid red' } : {}),
                }}
                InputProps={{
                  style: {
                    fontSize: '14px',
                    color: 'var(--space-primary-color)',
                  },
                }}
                variant="standard"
                value={quantity}
                onChange={(ev) => onChangeQuantity(ev.target.value)}
              />
            </Grid>
            <Grid item xs={6} style={{ display: 'flex', padding: '5px 0' }}>
              <p style={{ margin: 'auto 0' }}>Preparation:</p>
            </Grid>
            <Grid item xs={6} style={{ display: 'flex', padding: '5px 0' }}>
              <TextField
                style={{ margin: 'auto 0' }}
                variant="standard"
                fullWidth
                InputProps={{
                  style: {
                    fontSize: '14px',
                    color: 'var(--space-primary-color)',
                  },
                }}
                value={preparation}
                onChange={(ev) => onChangePreparation(ev.target.value)}
              />
            </Grid>
            <Grid item xs={12} style={{ display: 'flex', paddingTop: '10px' }}>
              <Button
                variant="contained"
                aria-label="rewrite"
                color="primary"
                style={{
                  padding: '4px',
                  width: '32px',
                  height: '32px',
                  minWidth: '32px',
                  borderRadius: '6px',
                  display: 'flex',
                }}
                sx={{
                  '& .MuiButton-startIcon': {
                    margin: 'auto',
                  },
                }}
                onClick={(ev) => {
                  onClickRewriteIngredientLine();
                  ev.stopPropagation();
                }}
                startIcon={
                  <ReplayIcon
                    style={{ width: '24px', height: '24px', margin: 'auto' }}
                  />
                }
              />
              <p style={{ margin: 'auto 0 auto 8px' }}>
                Rewrite ingredient line
              </p>
            </Grid>
            <Grid item xs={12}>
              <h4>Nutrition macros</h4>
            </Grid>
            <Grid item xs={12}>
              {nutritionOneLineSummary(nutrition)}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container>
            <Grid item xs={12} style={{ margin: 'auto 10px', padding: '6px' }}>
              <Button
                variant="contained"
                onClick={onClickUpdate}
                style={{ marginRight: '8px' }}
                disabled={!isValid}>
                Update
              </Button>
              <Button variant="contained" onClick={onDismiss} color="secondary">
                Cancel
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Grid>
    </Grid>
  );
};

IngredientStructuredDataEditHomePage.propTypes = {
  fullText: PropTypes.string.isRequired,
  linkedIngredientName: PropTypes.string.isRequired,
  structuredIngredientName: PropTypes.string.isRequired,
  unitOfMeasure: PropTypes.string.isRequired,
  supportedMeasures: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  quantity: PropTypes.number.isRequired,
  preparation: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  nutrition: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  errors: PropTypes.object.isRequired,
  onDismiss: PropTypes.func.isRequired,
  onClickUpdate: PropTypes.func.isRequired,
  onClickRewriteIngredientLine: PropTypes.func.isRequired,
  onClickSearchIngredient: PropTypes.func.isRequired,
  onChangeStructuredIngredientName: PropTypes.func.isRequired,
  onChangeUnitOfMeasure: PropTypes.func.isRequired,
  onChangeQuantity: PropTypes.func.isRequired,
  onChangePreparation: PropTypes.func.isRequired,
};

export default IngredientStructuredDataEditHomePage;
